import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "../../../lib/auth";
import { checkpointChat } from "../api/chat-share.api";
import type { ChatMessage } from "./use-ai-chat";
import { reconcileRequestSent, subscribeRequestSignals } from "../lib/proposal-status";

/**
 * Conversation history for the AI chat: the browser always, the account too.
 *
 * localStorage stays the source of truth for what is on screen, because the
 * chat works before anyone signs in and a signed-out guest must not lose their
 * thread. On top of that, a signed-in guest's turns are checkpointed to
 * `concierge_sessions` — which is what makes a conversation resumable on
 * another device and gives it a `sessionId` to hang a share link off.
 *
 * The sync is deliberately one-way and best-effort. A failed checkpoint is
 * swallowed: the chat is in front of the guest and must not break because a
 * background save timed out.
 *
 * The same conversation list is shared between the homepage modal
 * (`GlassConcierge`) and the Discover-tab panel (`DiscoverChat`), so a
 * chat started in one surface can be resumed from the other. We persist
 * to `window.localStorage` under a single namespaced key. Each entry
 * stores the message thread plus a derived title from the first user
 * turn (truncated for the dropdown).
 *
 * Per-conversation save / in-trip state is not persisted here — those
 * live on the user's account once we wire them to the wishlist + trip
 * features. This hook only handles the chat thread itself.
 *
 * SSR-safe: the initial render reads from a default (empty) state; the
 * client hydrates from localStorage in an effect.
 */

export interface StoredConversation {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: ChatMessage[];
  /**
   * The row in `concierge_sessions` this conversation checkpoints into, once
   * the guest is signed in. Absent for signed-out chats, which live only in
   * this browser — and which therefore cannot be shared.
   */
  sessionId?: string;
}

interface StoredState {
  conversations: StoredConversation[];
  activeId: string | null;
}

const STORAGE_KEY = "mj_ai_chat_history_v1";
const MAX_CONVERSATIONS = 30;
const TITLE_MAX_LEN = 60;

function isBrowser(): boolean {
  return typeof window !== "undefined" && !!window.localStorage;
}

function loadFromStorage(): StoredState {
  if (!isBrowser()) return { conversations: [], activeId: null };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { conversations: [], activeId: null };
    const parsed = JSON.parse(raw) as StoredState;
    if (!Array.isArray(parsed.conversations)) {
      return { conversations: [], activeId: null };
    }
    return parsed;
  } catch {
    return { conversations: [], activeId: null };
  }
}

function saveToStorage(state: StoredState): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota / private-browsing — silent. We never want a save to break
    // the chat experience.
  }
}

function genId(): string {
  return `conv-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

/**
 * The transcript, in the API's shape. `slugs` are the catalogue rows the
 * assistant's cards showed on that turn — the recap's landed/shortlist
 * heuristic runs on them, so dropping them would quietly degrade recaps.
 *
 * Capped at the 40 turns the route accepts, keeping the most recent.
 */
function toApiTurns(messages: ChatMessage[]) {
  return messages
    .filter((m): m is Extract<ChatMessage, { kind: "user" | "assistant" }> =>
      m.kind === "user" || m.kind === "assistant",
    )
    .map((m) =>
      m.kind === "user"
        ? { role: "user" as const, content: m.text }
        : {
            role: "assistant" as const,
            content: m.text,
            ...(m.matches.length > 0
              ? { slugs: m.matches.slice(0, 12).map((x) => x.slug) }
              : {}),
          },
    )
    .filter((t) => t.content.trim().length > 0)
    .slice(-40);
}

/**
 * A cheap identity for a thread, used to tell "the guest said something" from
 * "the guest opened this to read it".
 *
 * Ids alone are not enough: a streamed reply grows inside one assistant
 * message, so the id list is unchanged while the text is not. The lengths
 * cover that without stringifying a thread on every tick of the stream.
 */
function threadSignature(messages: ChatMessage[]): string {
  // A turn that gained its "request sent" proof is a change worth saving.
  return messages
    .map(
      (m) =>
        `${m.id}:${"text" in m ? m.text.length : 0}${m.kind === "assistant" && m.requestSent ? ":sent" : ""}`,
    )
    .join("|");
}

/**
 * Whether a conversation led to a request the guest actually sent (C11), for
 * the "request sent" tag in the history menu.
 */
export function conversationRequestSent(c: Pick<StoredConversation, "messages">): boolean {
  return c.messages.some((m) => m.kind === "assistant" && !!m.requestSent);
}

/**
 * Stamp the proof of a sent request onto every saved conversation that now has
 * it. Same object back when nothing changed.
 */
function reconcileState(state: StoredState): StoredState {
  let changed = false;
  const conversations = state.conversations.map((c) => {
    const messages = reconcileRequestSent(c.messages);
    if (messages === c.messages) return c;
    changed = true;
    return { ...c, messages };
  });
  return changed ? { ...state, conversations } : state;
}

/**
 * The chat on screen does not know about a proof stamped into storage behind
 * its back, and its next save would wipe it. Carry it over by message id.
 */
function keepRequestSent(next: ChatMessage[], stored: ChatMessage[] | undefined): ChatMessage[] {
  if (!stored) return next;
  const sent = new Map<string, NonNullable<Extract<ChatMessage, { kind: "assistant" }>["requestSent"]>>();
  for (const m of stored) {
    if (m.kind === "assistant" && m.requestSent) sent.set(m.id, m.requestSent);
  }
  if (sent.size === 0) return next;
  return next.map((m) =>
    m.kind === "assistant" && !m.requestSent && sent.has(m.id)
      ? { ...m, requestSent: sent.get(m.id) }
      : m,
  );
}

function deriveTitle(messages: ChatMessage[]): string {
  const firstUser = messages.find((m) => m.kind === "user");
  if (!firstUser) return "New chat";
  const text = (firstUser as Extract<ChatMessage, { kind: "user" }>).text;
  return text.length > TITLE_MAX_LEN
    ? `${text.slice(0, TITLE_MAX_LEN).trim()}…`
    : text;
}

export interface UseChatHistoryState {
  conversations: StoredConversation[];
  activeId: string | null;
  activeMessages: ChatMessage[];
  /**
   * False on first render; flips to true once the hook has finished
   * reading localStorage. Consumers use this to know when it's safe to
   * hydrate their local chat state from `activeMessages`.
   */
  loaded: boolean;
  /** Persist a new message list into the active conversation (or create one). */
  persistMessages: (messages: ChatMessage[]) => void;
  /** Start an empty chat. Returns the new active id. */
  newChat: () => string;
  /** Switch to an existing conversation. Returns its messages, or [] if missing. */
  selectChat: (id: string) => ChatMessage[];
  /** Remove a conversation. If it was active, clear activeId. */
  deleteChat: (id: string) => void;
  /**
   * The server id of the active conversation, or null. Null means there is
   * nothing to share yet — either the guest is signed out, or the first
   * checkpoint has not come back.
   */
  activeSessionId: string | null;
}

export function useChatHistory(): UseChatHistoryState {
  const { data: session } = useSession();
  const signedIn = !!session?.user;
  const [state, setState] = useState<StoredState>({
    conversations: [],
    activeId: null,
  });
  const [loaded, setLoaded] = useState(false);
  // Hydrate from localStorage on the client. A request sent from the place's
  // form while the chat was closed is stamped on here, before any surface
  // reads the thread, so coming back to the chat shows "Request sent".
  useEffect(() => {
    const raw = loadFromStorage();
    const reconciled = reconcileState(raw);
    if (reconciled !== raw) saveToStorage(reconciled);
    setState(reconciled);
    setLoaded(true);
  }, []);

  // …and again whenever the proof can change while the chat is open: a
  // booking saved in another tab, or the guest coming back to this one.
  useEffect(() => {
    return subscribeRequestSignals(() => {
      const cur = stateRef.current;
      const reconciled = reconcileState(cur);
      if (reconciled === cur) return;
      stateRef.current = reconciled;
      setState(reconciled);
      saveToStorage(reconciled);
    });
  }, []);

  // Cross-tab sync: when another tab writes the same key, update here.
  useEffect(() => {
    if (!isBrowser()) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setState(loadFromStorage());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const activeMessages = useMemo(() => {
    if (!state.activeId) return [];
    const conv = state.conversations.find((c) => c.id === state.activeId);
    return conv?.messages ?? [];
  }, [state.activeId, state.conversations]);

  // Stale-state guard: keep a ref so async callbacks can read the latest
  // state without becoming dependencies of memoized callbacks (which
  // would invalidate them and break consumer effects).
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Read inside `persistMessages` without making it a dependency: the
  // callback is memoized with an empty dep list on purpose, because consumers
  // hold it in effects that must not re-run.
  const signedInRef = useRef(signedIn);
  useEffect(() => {
    signedInRef.current = signedIn;
  }, [signedIn]);

  const persistMessages = useCallback((messages: ChatMessage[]) => {
    // Skip persisting transient "pending" turns — they vanish as soon as
    // the API responds and would dirty the cross-tab sync for nothing.
    const withoutPending = messages.filter((m) => m.kind !== "pending");
    if (withoutPending.length === 0) return;

    const cur = stateRef.current;
    const now = Date.now();
    const id = cur.activeId ?? genId();
    const existing = cur.conversations.find((c) => c.id === id);
    const clean = keepRequestSent(withoutPending, existing?.messages);

    // Opening a saved chat hands its own messages straight back here. Writing
    // regardless stamped `updatedAt: now` on a conversation nobody had added
    // to — so every row in the menu read "just now" and the list ordered by
    // last opened instead of last used.
    if (existing && threadSignature(existing.messages) === threadSignature(clean)) {
      return;
    }

    const next: StoredConversation = {
      id,
      title: deriveTitle(clean),
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      messages: clean,
    };
    const rest = cur.conversations.filter((c) => c.id !== id);
    // Newest first, capped.
    const conversations = [next, ...rest].slice(0, MAX_CONVERSATIONS);
    const newState: StoredState = { conversations, activeId: id };
    setState(newState);
    saveToStorage(newState);

    // …and, for a signed-in guest, onto the account. Fire and forget: the
    // thread on screen is already saved locally, so a slow or failed save
    // must not be something the guest can feel.
    if (!signedInRef.current) return;
    const turns = toApiTurns(clean);
    if (turns.length === 0) return;
    void checkpointChat({ sessionId: existing?.sessionId, turns })
      .then((sessionId) => {
        if (!sessionId) return;
        setState((prev) => {
          const withId = prev.conversations.map((c) =>
            c.id === id ? { ...c, sessionId } : c,
          );
          const merged: StoredState = { ...prev, conversations: withId };
          saveToStorage(merged);
          return merged;
        });
      })
      .catch(() => {
        // Swallowed on purpose — see the note at the top of the file.
      });
  }, []);

  const newChat = useCallback((): string => {
    const id = genId();
    const newState: StoredState = {
      conversations: stateRef.current.conversations,
      activeId: null, // will be set when first message persists
    };
    setState(newState);
    saveToStorage(newState);
    return id;
  }, []);

  const selectChat = useCallback((id: string): ChatMessage[] => {
    const cur = stateRef.current;
    const conv = cur.conversations.find((c) => c.id === id);
    const newState: StoredState = { ...cur, activeId: conv ? id : null };
    setState(newState);
    saveToStorage(newState);
    return conv?.messages ?? [];
  }, []);

  const deleteChat = useCallback((id: string) => {
    const cur = stateRef.current;
    const conversations = cur.conversations.filter((c) => c.id !== id);
    const activeId = cur.activeId === id ? null : cur.activeId;
    const newState: StoredState = { conversations, activeId };
    setState(newState);
    saveToStorage(newState);
  }, []);

  const activeSessionId = useMemo(() => {
    if (!state.activeId) return null;
    return state.conversations.find((c) => c.id === state.activeId)?.sessionId ?? null;
  }, [state.activeId, state.conversations]);

  return {
    conversations: state.conversations,
    activeId: state.activeId,
    activeMessages,
    activeSessionId,
    loaded,
    persistMessages,
    newChat,
    selectChat,
    deleteChat,
  };
}
